import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex, FlexProps } from "@chakra-ui/react";
import { useIsCollapsed } from "~/hooks/useIsCollapsed";
import { RadioInput } from "./RadioInput";
import { SupportingText } from "./SupportingText";

export type Scale = "" | "1" | "2" | "3" | "4" | "5";

type ScalePath =
  | "onboarding.clinicSatisfactionPage"
  | "onboarding.howAnxiousPage"
  | "onboarding.howGratefulPage"
  | "onboarding.howOptimisticPage"
  | "onboarding.howSupportedPage";
interface Props {
  autoAdvance?: boolean;
  defaultValue?: Scale;
  id: string;
  label?: string;
  scalePath: ScalePath;
}

const values: Scale[] = ["1", "2", "3", "4", "5"];
const mappedValues: string[] = values.map((value) => value.toString());

const radioGroupProps: FlexProps = {
  direction: { base: "column", md: "row" },
  gap: { base: 4, md: 2 },
};

const Label: FC<{ scalePath: ScalePath; value: "lowest" | "highest" }> = ({
  scalePath,
  value,
}) => {
  const { t } = useTranslation();
  const scaleVal = `${scalePath}.scale.${value}` as const;
  return <SupportingText>{t(scaleVal)}</SupportingText>;
};

export const ScaleInput: FC<Props> = ({
  autoAdvance,
  defaultValue,
  id,
  label,
  scalePath,
}) => {
  const isCollapsed = useIsCollapsed({ base: true, md: false });

  return (
    <Flex direction="column" justify="center" w="full">
      {isCollapsed && (
        <Box mb={2}>
          <Label scalePath={scalePath} value="lowest" />
        </Box>
      )}
      <Box>
        <RadioInput
          autoAdvance={autoAdvance}
          defaultValue={defaultValue?.toString()}
          id={id}
          label={label}
          radioGroupProps={radioGroupProps}
          values={mappedValues}
        />
      </Box>
      {isCollapsed && (
        <Box mt={2}>
          <Label scalePath={scalePath} value="highest" />
        </Box>
      )}

      {!isCollapsed && (
        <Flex justify="space-between" mt={2} w="full">
          <Label scalePath={scalePath} value="lowest" />

          <Label scalePath={scalePath} value="highest" />
        </Flex>
      )}
    </Flex>
  );
};
